import React, { useCallback, useMemo } from 'react';

import { FlatList, StyleSheet } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import { useCart } from 'hooks/use-cart';
import { IncentiveCard } from 'pages/loyalty/loyalty-incentives-components/incentive-card/incentive-card';
import { LoyaltyRewardsEmptyScreen } from 'pages/loyalty/loyalty-rewards/empty-screen/loyalty-rewards-empty-screen';
import { LoyaltyRewardsMeter } from 'pages/loyalty/loyalty-rewards/loyalty-rewards-meter';
import {
  CategoryLabel,
  CrownIcon,
  RewardCategoryCarouselContainer,
  RewardCategoryContainer,
  RewardCategoryHeader,
} from 'pages/loyalty/loyalty-rewards/loyalty-rewards.styled';
import {
  IAppliedReward,
  useAppliedRewards,
} from 'pages/loyalty/loyalty-rewards/use-applied-reward';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { spacing } from 'styles/constants';
import { isMobile } from 'utils/environment';

import { Reward, RewardCategory } from '../types';
import { isRewardVisibleInBrowse, orderRewardsForBrowse } from '../utils/browse';
import { getRewardCooldownAlertMessage } from '../utils/cooldown';

interface ServiceRewardsListProps {
  categories: RewardCategory[];
  handleOnPress: (reward: Reward) => void;
}

const getAppliedRewardEngineId = (reward: IAppliedReward): string =>
  reward.loyaltyEngineId || reward.rewardId;

const NO_APPLIED_REWARDS: IAppliedReward[] = [];
const NO_APPLIED_REWARD_IDS: ReadonlySet<string> = new Set();

export const ServiceRewardsList = ({ categories, handleOnPress }: ServiceRewardsListProps) => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const { rewardsCooldown } = useLoyaltyContext();
  const appliedRewards = useAppliedRewards();
  const effectiveAppliedRewards = enableRewardUnstacking ? appliedRewards : NO_APPLIED_REWARDS;
  const cooldownAlertMessage = getRewardCooldownAlertMessage(rewardsCooldown);
  const appliedRewardIds = useMemo(
    () =>
      enableRewardUnstacking
        ? new Set(effectiveAppliedRewards.map(getAppliedRewardEngineId))
        : NO_APPLIED_REWARD_IDS,
    [effectiveAppliedRewards, enableRewardUnstacking]
  );

  const visibleCategories = useMemo(
    () =>
      categories
        .map(category => ({
          category,
          rewards: orderRewardsForBrowse(category.rewards, appliedRewardIds),
        }))
        .filter(({ rewards }) => rewards.length > 0),
    [appliedRewardIds, categories]
  );

  const firstLockedCategory = visibleCategories.find(({ rewards }) =>
    rewards.some(reward => reward.locked)
  );

  if (!visibleCategories.length) {
    return <LoyaltyRewardsEmptyScreen />;
  }

  return (
    <>
      {visibleCategories.map(({ category, rewards }) => (
        <ServiceRewardCategory
          key={category.label}
          appliedRewardIds={appliedRewardIds}
          appliedRewards={effectiveAppliedRewards}
          category={category}
          cooldownAlertMessage={cooldownAlertMessage}
          handleOnPress={handleOnPress}
          inCooldown={rewardsCooldown.isActive}
          rewards={rewards}
          showMeter={firstLockedCategory?.category === category}
        />
      ))}
    </>
  );
};

interface ServiceRewardCategoryProps {
  appliedRewardIds: ReadonlySet<string>;
  appliedRewards: IAppliedReward[];
  category: RewardCategory;
  cooldownAlertMessage?: string;
  handleOnPress: (reward: Reward) => void;
  inCooldown: boolean;
  rewards: Reward[];
  showMeter: boolean;
}

const ServiceRewardCategory = ({
  appliedRewardIds,
  appliedRewards,
  category,
  cooldownAlertMessage,
  handleOnPress,
  inCooldown,
  rewards,
  showMeter,
}: ServiceRewardCategoryProps) => {
  const { cartEntries, removeFromCart } = useCart();
  const { isAuthenticated } = useAuthContext();

  const categoryLocked = rewards.some(reward => reward.locked);
  const meterReward = rewards.find(reward => reward.locked);
  const categoryHeaderAccessibilityHint = `${category.label} ${t('crowns')}`;

  const unavailableCartEntries = useMemo<ILegacyCartEntry[]>(() => {
    const hiddenRewardIds = new Set(
      category.rewards.filter(reward => !isRewardVisibleInBrowse(reward)).map(reward => reward.id)
    );
    return appliedRewards.flatMap(appliedReward => {
      if (!hiddenRewardIds.has(getAppliedRewardEngineId(appliedReward))) {
        return [];
      }
      const cartEntry = cartEntries.find(entry => entry.cartId === appliedReward.cartId);
      return cartEntry ? [cartEntry] : [];
    });
  }, [appliedRewards, cartEntries, category.rewards]);

  const handleRemoveUnavailableItems = useCallback(() => {
    unavailableCartEntries.forEach(entry => removeFromCart({ cartId: entry.cartId }));
  }, [removeFromCart, unavailableCartEntries]);

  const renderReward = useCallback(
    ({ item: reward }: { item: Reward }) => (
      <IncentiveCard
        incentive={reward}
        handlePress={handleOnPress}
        locked={!isAuthenticated || Boolean(reward.locked)}
        inlineAlertMessage={cooldownAlertMessage ?? reward.inlineAlertMessage}
        isPictureBlurhashEnabled={false}
        isApplied={appliedRewardIds.has(reward.id)}
        inCooldown={inCooldown}
      />
    ),
    [appliedRewardIds, cooldownAlertMessage, handleOnPress, inCooldown, isAuthenticated]
  );

  const rewardCards = isMobile() ? (
    <FlatList
      showsHorizontalScrollIndicator={false}
      horizontal
      style={styles.flatList}
      contentContainerStyle={styles.contentContainer}
      data={rewards}
      keyExtractor={reward => reward.id}
      overScrollMode="never"
      renderItem={renderReward}
    />
  ) : (
    <RewardCategoryCarouselContainer>
      <ResponsiveCarousel
        loop={false}
        itemWidth={168}
        data={rewards}
        keyExtractor={reward => reward.id}
        renderItem={renderReward}
      />
    </RewardCategoryCarouselContainer>
  );

  return (
    <>
      {showMeter && meterReward ? (
        <LoyaltyRewardsMeter
          redemptionPercentage={meterReward.redemptionPercentage || 0}
          remainingPointsNeededForRedemption={
            meterReward.remainingPointsNeededForRedemption || undefined
          }
        />
      ) : null}

      <RewardCategoryContainer locked={categoryLocked} accessible={false}>
        <RewardCategoryHeader>
          <CrownIcon />
          <CategoryLabel accessibilityLabel={categoryHeaderAccessibilityHint}>
            {category.label}
          </CategoryLabel>
        </RewardCategoryHeader>
        {rewardCards}
      </RewardCategoryContainer>

      {unavailableCartEntries.length > 0 ? (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleRemoveUnavailableItems}
          unavailableCartEntries={unavailableCartEntries}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  flatList: {
    paddingLeft: spacing.$4,
    flex: 1,
  },
  contentContainer: {
    paddingRight: 24,
  },
});
